import { noop } from 'lodash';
import React, { Context, createContext, memo, ReactElement, ReactNode, useContext, useState } from 'react';
import { useCookies } from 'react-cookie';
import EmailValidationModal from '../components/EmailValidationModal';
import { emitNotification } from '../components/Notification';
import { fetchFromRestAPI, useAuthFetchFromRestAPI } from './api';
import { Auth0ContextInterface, useAuth0, User } from '@auth0/auth0-react';
import { fetchUserAccountRemote } from './UserAccount';
import { auth0EmailVerifiedKey } from '../../../shared/consts';
import { AuthPermission } from '../../../shared/utils/enums';

type ExtendedAuthContext = {
  userId: string | null;
  isAuthenticated: boolean;
  
  isAdminUser: () => Promise<boolean>,
  isSuperAdminUser: () => Promise<boolean>,
  isApprovedTherapist: () => Promise<boolean>,
  
  handleOnAuthenticated: (accessToken: string, user: User) => void,
  handleOnSignedIn: () => void,
}

export const AUTH0_SCOPE: string = 'read:patientdata write:patientdata access:webapp access:tenantadmin access:superadmin';

export const isEmailVerificationPending = (user: User): boolean => {
  const isAuth0DbUser: boolean = Boolean(user.sub?.startsWith('auth0|'));
  return isAuth0DbUser && user[auth0EmailVerifiedKey] === false;
};

const ExtendedAuthContext: Context<ExtendedAuthContext> = createContext<ExtendedAuthContext>({
  userId: null,
  isAuthenticated: false,
  isAdminUser: () => Promise.resolve(false),
  isSuperAdminUser: () => Promise.resolve(false),
  isApprovedTherapist: () => Promise.resolve(false),
  handleOnAuthenticated: noop,
  handleOnSignedIn: noop,
});

export const useExtendedAuth = (): ExtendedAuthContext => {
  const context: ExtendedAuthContext = useContext(ExtendedAuthContext);
  if (!context) {
    throw new Error('useExtendedAuth must be used within ExtendedAuthProvider');
  }
  return context;
};

const Auth0ProviderMemo = ({ children }: { children: ReactNode } ): ReactElement => {

  const auth0: Auth0ContextInterface = useAuth0();
  const { authFetch } = useAuthFetchFromRestAPI();

  const [requestedVerificationEMail, setRequestedVerificationEMail] = useState(false);
  const [lastAuthToken, setLastAuthToken] = useState<string | null>(null);
  const [emailValidationModalShow, setEmailValidationModalShow] = useState(false);
  const [cookies, setCookie, removeCookie] = useCookies<string>([]);
  
  const userId: string | null = auth0.user?.sub ?? null;
  const isAuthenticated: boolean = Boolean(auth0.isAuthenticated);

  // Not used since no distinction between signon and login possible
  const handleOnSignedIn = (): void => {
    emitNotification(
      <div>
        <big>
          Wir haben dir soeben eine E-Mail geschickt. Bitte bestätige deine E-Mail Adresse, damit du enlivio nutzen kannst.
        </big>
        <br />
        <br />
        Solltest du Fragen haben, schicke uns eine E-mail an{' '}
        <a href="mailto:info@enlivio.com">info@enlivio.com</a>
      </div>,
      {
        position: 'top-center',
        autoClose: 20000,
        hideProgressBar: false,
        closeOnClick: true,
        pauseOnHover: true,
        draggable: true,
        progress: undefined,
      }
    );
  };

  const cleanAuthCookies = (): void => {
    if (!cookies) {
      return;
    }
    // We need to clean auth0 cookies for old sessions and in case of using not main domain like staging
    const cookiesKeys: string[] = Object.keys(cookies);
    
    cookiesKeys.forEach((cookie: string) => {
      if (cookie.includes('auth0') || cookie.includes('did')) {
        setCookie(cookie, '', { expires: new Date() });
        removeCookie(cookie);
      }
    });
  };

  const clearSessionLocalStorage = (): void  => {
    cleanAuthCookies();
  };

  const extendedLogout = async (): Promise<void> => {
    clearSessionLocalStorage();

    await auth0.logout({ logoutParams: { returnTo: window.location.origin } });
  };

  const resendVerificationMail = async (token: string | null): Promise<void> => {
    if (!token) {
      return;
    }
    
    const response = await fetchFromRestAPI('/api/v1/user-profile/verification-email', {
      method: 'POST',
      token: token,
    });
    
    if (response.ok) {
      setRequestedVerificationEMail(true);
    }
  };

  const handleOnAuthenticated = (accessToken: string, user: User): void => {
    if (isEmailVerificationPending(user)) {
      setLastAuthToken(accessToken);
      setEmailValidationModalShow(true);
      clearSessionLocalStorage();
    }
  };
  

  const isAdminUserBasedOnPermissions = async (): Promise<boolean> => {
    const permissions: AuthPermission[] = await authFetch('/api/v1/token/permissions');
    return Array.isArray(permissions) && permissions.includes(AuthPermission.ACCESS_TENANT_ADMIN);
  };

  const isSuperAdminUserBasedOnPermissions = async (): Promise<boolean> => {
    const permissions: AuthPermission[] = await authFetch('/api/v1/token/permissions');
    return Array.isArray(permissions) && permissions.includes(AuthPermission.ACCESS_SUPER_ADMIN);
  };

  const isApprovedTherapist = async (): Promise<boolean> => {
    const userProfileResponse = await fetchUserAccountRemote(authFetch);
    return !!userProfileResponse && userProfileResponse.approved;
  };
  
  return (
    <ExtendedAuthContext.Provider
      value={{
        userId,
        isAuthenticated,
        isAdminUser: isAdminUserBasedOnPermissions,
        isSuperAdminUser: isSuperAdminUserBasedOnPermissions,
        isApprovedTherapist,
        handleOnSignedIn: handleOnSignedIn,
        handleOnAuthenticated: handleOnAuthenticated,
      }}
    >
      {children}
      
      <EmailValidationModal
        show={emailValidationModalShow}
        sent={requestedVerificationEMail}
        onPositive={() => {
          resendVerificationMail(lastAuthToken);
          
          setTimeout(() => {
            setEmailValidationModalShow(false);
            setLastAuthToken(null);
            setRequestedVerificationEMail(false);
            extendedLogout();
          }, 1000);
        }}
        onNegative={ async () => {
          setEmailValidationModalShow(false);
          setLastAuthToken(null);
          setRequestedVerificationEMail(false);
          await extendedLogout();
        }}
      />
    </ExtendedAuthContext.Provider>
  );
};

export const ExtendedAuthProvider = memo(Auth0ProviderMemo);
